import React, { useState, useEffect } from 'react';
import { fetchUsersWithProfiles } from '../services/userService';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { ShimmerTable, ShimmerBox } from '../components/common/Shimmer';
import { PageHeader } from '@/components/ui/page-header';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';

const Users = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchUsersWithProfiles(token)
      .then(setUsers)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleBlockToggle = (id) => {
    // TODO: Implement block/unblock API call
    setUsers(
      users.map((user) => (user.id === id ? { ...user, banned: !user.banned } : user))
    );
  };

  const filteredUsers = users.filter((user) => {
    const userName =
      user.full_name ||
      user.user_metadata?.full_name ||
      user.raw_user_meta_data?.full_name ||
      user.name ||
      '';
    return (
      user.email?.toLowerCase().includes(search.toLowerCase()) ||
      userName.toLowerCase().includes(search.toLowerCase()) ||
      user.user_metadata?.phone?.includes(search) ||
      user.phone?.includes(search)
    );
  });

  const getUserName = (user) =>
    user.full_name ||
    user.user_metadata?.full_name ||
    user.raw_user_meta_data?.full_name ||
    user.name ||
    user.email?.split('@')[0] ||
    '-';

  return (
    <div className="px-2 sm:px-0">
      <PageHeader title={t('users')} />
      <div className="mb-4">
        <Input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:max-w-md"
        />
      </div>
      {loading ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <ShimmerBox width="w-64" height="h-10" />
            <ShimmerBox width="w-32" height="h-10" />
          </div>
          <ShimmerTable rows={8} columns={7} />
        </div>
      ) : error ? (
        <div className="py-8 text-center text-destructive">{error}</div>
      ) : (
        <>
          <Card className="hidden lg:block">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('name')}</TableHead>
                    <TableHead>{t('email')}</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>{t('role')}</TableHead>
                    <TableHead>{t('status')}</TableHead>
                    <TableHead>{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">
                        {search ? t('noResultsFound') : t('noUsersFound')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{getUserName(user)}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.phone || user.user_metadata?.phone || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{user.user_metadata?.role || '-'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.banned ? 'destructive' : 'success'}>
                            {user.banned ? t('blocked') : t('active')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={user.banned ? 'default' : 'destructive'}
                            onClick={() => handleBlockToggle(user.id)}
                          >
                            {user.banned ? t('unblock') : t('block')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="space-y-4 lg:hidden">
            {filteredUsers.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                {search ? t('noResultsFound') : t('noUsersFound')}
              </div>
            ) : (
              filteredUsers.map((user) => (
                <Card key={user.id}>
                  <CardContent className="p-4">
                    <div className="mb-3 flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-foreground">{getUserName(user)}</h3>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                      <Badge variant={user.banned ? 'destructive' : 'success'}>
                        {user.banned ? t('blocked') : t('active')}
                      </Badge>
                    </div>

                    <div className="mb-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Phone:</span>
                        <span>{user.phone || user.user_metadata?.phone || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Role:</span>
                        <Badge variant="secondary">{user.user_metadata?.role || '-'}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">User ID:</span>
                        <span className="font-mono text-xs">{user.id.substring(0, 8)}...</span>
                      </div>
                    </div>

                    <Button
                      className="w-full"
                      variant={user.banned ? 'default' : 'destructive'}
                      onClick={() => handleBlockToggle(user.id)}
                    >
                      {user.banned ? t('unblock') : t('block')}
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Users;
